import axios from 'axios';
import urlJoin from 'url-join';

/**
 * XHR request helpers. Wrap axios
 */
export default class HTTPService {
    /**
     * Method to retrieve map feature url
     * @param {String} url     Feature url
     * @param {String} bounds  Feature bounds param
     * @return {String}
     */
    static getMapFeatureURL(url, bounds) {
        return urlJoin(url, `?bounds=${bounds}`);
    }

    /**
     * Method to retrieve place url
     * @param {Object} place
     * @return {String}
     */
    static getPlaceURL(place) {
        const pricesRoute = '/prix-immobilier';

        if (place) {
            if (place.slug) {
                return urlJoin(pricesRoute, place.slug);
            }
            const {
                city_slug: citySlug,
                street_slug: streetSlug,
                street_id: streetId,
                address_slug: addressSlug,
            } = place;
            let urlParts = [pricesRoute];

            if (citySlug) {
                urlParts = [...urlParts, citySlug];
            }

            if (streetSlug) {
                urlParts = [...urlParts, `${streetSlug}-${streetId}`];
            }

            if (addressSlug) {
                urlParts = [...urlParts, addressSlug];
            }

            return urlJoin(...urlParts);
        }

        return urlJoin(pricesRoute);
    }

    /**
     * Method to retrieve static file url
     * @param {String} filename Static filename
     * @return {String}
     */
    static getStaticFileURL(filename) {
        return urlJoin('/', 'static', filename);
    }

    /**
     * Call WWW app with GET method to grab JSON
     * @param {String} url Path URL of request
     * @param {Object} params Query params
     * @param {Object} options Axios option of request
     * @return {Promise<Object>}
     */
    static get(url, params = {}, options = {}) {
        return axios.get(url, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest', // Needed by FLask
            },
            params,
            ...options,
        });
    }

    /**
     * Call WWW app with GET method to grab HTML
     * @param {String} url Path URL of request
     * @param {Object} params Query params
     * @param {Object} options Axios option of request
     * @return {Promise<Object>}
     */
    static getHTML(url, params = {}, options = {}) {
        return axios.get(url, {
            headers: {
                Accept: 'text/html',
                'Content-Type': 'text/html',
                'X-Requested-With': 'XMLHttpRequest', // Needed by FLask
            },
            params,
            ...options,
        });
    }

    /**
     * Call WWW app with DELETE method
     * @param {String} url Path URL of request
     * @param {Object} options Axios option of request
     * @return {Promise<Object>}
     */
    static delete(url, options = {}) {
        return axios.delete(url, options);
    }

    /**
     * Call WWW app with PUT method
     * @param {String} url Path URL of request
     * @param {Object} data Request data
     * @param {Object} options Axios option of request
     * @return {Promise<Object>}
     */
    static put(url, data = {}, options = {}) {
        return axios.put(url, data, options);
    }

    /**
     * Call WWW app with POST method
     * @param {String} url Path URL of request
     * @param {Object} data Request data
     * @param {Object} options Axios option of request
     * @return {Promise<Object>}
     */
    static post(url, data = {}, options = {}) {
        return axios.post(url, data, options);
    }

    /**
     * Call Indice API with GET method.
     * @param {String} url Path URL of request
     * @return {Promise<Object>}
     */
    static getIndice(url, params = {}, options = {}) {
        return axios.get(urlJoin(MA.Config.INDICEAPI_URL, url), {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            params,
            ...options,
        });
    }

    /**
     * Call Geo API with GET method.
     * @param {String} url Path URL of request
     * @return {Promise<Object>}
     */
    static getGeo(url, params = {}, options = {}) {
        return axios
            .get(urlJoin(MA.Config.GEOAPI_URL, url), {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                params,
                ...options,
            })
            .then(response => ({
                ...response,
                data: response.data.response, // Remove useless API response JSON level
            }));
    }
}
